import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import Modal from './Modal';
import SeatBillingConfirmModal from './SeatBillingConfirmModal';
import { Box, TextField, Grid, Button, MenuItem } from '@mui/material';
import { makeApiRequest } from '../../Shared/Api';
import { useAppContext } from '../../Context/AppContext';
import { useFeature } from '../../Hooks/useFeature';

const EMPTY_FORM = {
	first_name: '',
	last_name: '',
	email: '',
	role: 'Employee',
	organization: '',
	hourly_cost: '',
};

const EditUser = ({ open, onClose, onSubmit, selectedUser }) => {
	const { enqueueSnackbar } = useSnackbar();
	const { state } = useAppContext();
	const hasTeamAccounts = useFeature('team_accounts');
	const isSuperuser = state.user.isSuperuser;
	const [formData, setFormData] = useState(EMPTY_FORM);
	const [organizations, setOrganizations] = useState([]);
	const [billingPreview, setBillingPreview] = useState(null);
	const [billingModalOpen, setBillingModalOpen] = useState(false);
	const [pendingPayload, setPendingPayload] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const isEditing = Boolean(selectedUser);

	useEffect(() => {
		if (selectedUser) {
			setFormData({
				first_name: selectedUser.first_name || '',
				last_name: selectedUser.last_name || '',
				email: selectedUser.email || selectedUser.username || '',
				role: selectedUser.role || 'Employee',
				organization: selectedUser.organization?.id || '',
				hourly_cost:
					selectedUser.hourly_cost != null && selectedUser.hourly_cost !== ''
						? String(selectedUser.hourly_cost)
						: '',
			});
		} else {
			setFormData(EMPTY_FORM);
		}
	}, [selectedUser]);

	useEffect(() => {
		if (!open || !isSuperuser) {
			setOrganizations([]);
			return;
		}

		makeApiRequest('GET', '/auth/organizations/')
			.then((response) => setOrganizations(response || []))
			.catch((error) => {
				enqueueSnackbar(error.message || 'Failed to load organizations.', {
					variant: 'error',
				});
			});
	}, [open, isSuperuser, enqueueSnackbar]);

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const buildPayload = (confirmBilling = false) => {
		const email = formData.email.trim().toLowerCase();
		const payload = {
			email,
			first_name: formData.first_name,
			last_name: formData.last_name,
		};

		// Org managers can only add/edit employees. Superusers keep role control.
		if (isSuperuser) {
			payload.role = formData.role || 'Employee';
		} else if (!isEditing) {
			payload.role = 'Employee';
		}

		// Organization assignment is superuser-only.
		if (isSuperuser && formData.organization) {
			payload.organization = formData.organization;
		}

		const hourlyRaw = String(formData.hourly_cost ?? '').trim();
		if (hourlyRaw === '') {
			payload.hourly_cost = null;
		} else {
			const hourly = Number(hourlyRaw);
			if (Number.isNaN(hourly) || hourly < 0) {
				throw new Error('Hourly cost must be a non-negative number.');
			}
			payload.hourly_cost = hourly;
		}

		if (confirmBilling) {
			payload.confirm_billing = true;
		}

		return payload;
	};

	const submitUser = async (payload) => {
		if (isEditing) {
			await makeApiRequest('PATCH', `/auth/users/${selectedUser.id}/`, payload);
		} else {
			await makeApiRequest('POST', '/auth/users/', payload);
		}
		if (onSubmit) {
			onSubmit();
		}
		onClose();
	};

	const handleSubmit = async () => {
		setSubmitting(true);
		try {
			let payload;
			try {
				payload = buildPayload(false);
			} catch (validationError) {
				enqueueSnackbar(validationError.message, { variant: 'error' });
				return;
			}
			if (!payload.email) {
				enqueueSnackbar('Email is required.', { variant: 'error' });
				return;
			}
			if (isSuperuser && !isEditing && !payload.organization) {
				enqueueSnackbar('Organization is required.', { variant: 'error' });
				return;
			}
			if (isEditing || isSuperuser || !hasTeamAccounts) {
				await submitUser(payload);
				return;
			}

			try {
				await makeApiRequest('POST', '/auth/users/', payload);
				if (onSubmit) {
					onSubmit();
				}
				onClose();
			} catch (error) {
				if (error.status === 402 && error.data?.billing_preview) {
					setPendingPayload(payload);
					setBillingPreview(error.data.billing_preview);
					setBillingModalOpen(true);
					return;
				}
				throw error;
			}
		} catch (error) {
			enqueueSnackbar(
				error.message || `Failed to ${isEditing ? 'update' : 'create'} user.`,
				{ variant: 'error' },
			);
		} finally {
			setSubmitting(false);
		}
	};

	const handleConfirmBilling = async () => {
		if (!pendingPayload) {
			return;
		}
		setSubmitting(true);
		try {
			await makeApiRequest('POST', '/auth/users/', {
				...pendingPayload,
				confirm_billing: true,
			});
			setBillingModalOpen(false);
			setPendingPayload(null);
			setBillingPreview(null);
			if (onSubmit) {
				onSubmit();
			}
			onClose();
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to create user.', {
				variant: 'error',
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
			<Modal
				open={open}
				onClose={onClose}
				useActionButtons
				actionButtons={
					<Button
						variant="contained"
						onClick={handleSubmit}
						disabled={submitting}
					>
						{isEditing ? 'Save User' : 'Create User'}
					</Button>
				}
				modalTitle={isEditing ? 'Edit User' : 'Add User'}
				maxWidth="sm"
			>
				<Box sx={{ mt: 2 }}>
					<Grid container spacing={2}>
						<TextField
							label="First name"
							name="first_name"
							value={formData.first_name}
							onChange={handleInputChange}
							fullWidth
						/>
						<TextField
							label="Last name"
							name="last_name"
							value={formData.last_name}
							onChange={handleInputChange}
							fullWidth
						/>
						<TextField
							label="Email"
							name="email"
							type="email"
							value={formData.email}
							onChange={handleInputChange}
							fullWidth
							required
							helperText="Used to sign in."
						/>
						{isSuperuser ? (
							<TextField
								select
								label="Role"
								name="role"
								value={formData.role}
								onChange={handleInputChange}
								fullWidth
								required
							>
								<MenuItem value="Manager">Manager</MenuItem>
								<MenuItem value="Employee">Employee</MenuItem>
							</TextField>
						) : null}
						{isSuperuser ? (
							<TextField
								select
								label="Organization"
								name="organization"
								value={formData.organization}
								onChange={handleInputChange}
								fullWidth
								required={!isEditing}
							>
								{organizations.map((org) => (
									<MenuItem key={org.id} value={org.id}>
										{org.name}
									</MenuItem>
								))}
							</TextField>
						) : null}
						<TextField
							label="Hourly cost"
							name="hourly_cost"
							type="number"
							value={formData.hourly_cost}
							onChange={handleInputChange}
							fullWidth
							inputProps={{ min: 0, step: '0.01' }}
							helperText="Internal labor cost for analytics. Leave blank if unknown."
						/>
						{!isEditing && (
							<Grid item xs={12}>
								<Box sx={{ typography: 'caption', color: 'text.secondary' }}>
									New users receive the default password: password
								</Box>
							</Grid>
						)}
					</Grid>
				</Box>
			</Modal>

			<SeatBillingConfirmModal
				open={billingModalOpen}
				onClose={() => {
					setBillingModalOpen(false);
					setPendingPayload(null);
					setBillingPreview(null);
				}}
				onConfirm={handleConfirmBilling}
				title="Confirm additional seat"
				description="Adding this user will increase your subscription. Review the cost before confirming."
				preview={billingPreview}
				loading={submitting}
				confirmLabel="Add user"
			/>
		</>
	);
};

export default EditUser;
